import { useEffect, useState } from "react";
import { Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";

const THEME_META_COLORS = {
  light: "#f9f8f6",
  dark: "#111917",
} as const;

interface ThemeSwitcherProps {
  className?: string;
}

export function ThemeSwitcher({ className }: ThemeSwitcherProps) {
  const [mounted, setMounted] = useState(false);
  const { resolvedTheme, setTheme, theme } = useTheme();
  const { t } = useLanguage();

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!resolvedTheme) {
      return;
    }

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute(
        "content",
        resolvedTheme === "dark" ? THEME_META_COLORS.dark : THEME_META_COLORS.light,
      );
    }
  }, [resolvedTheme]);

  if (!mounted) {
    return null;
  }

  const selectedTheme = theme === "dark" ? "dark" : "light";

  return (
    <div className={cn("space-y-2", className)}>
      <p className="text-xs font-medium uppercase tracking-[0.18em] text-clinic-text-muted">
        {t("Tema")}
      </p>
      <Select value={selectedTheme} onValueChange={setTheme}>
        <SelectTrigger
          aria-label={t("Tema da interface")}
          className="h-11 w-full rounded-xl border-clinic-border bg-clinic-surface text-clinic-text"
        >
          <SelectValue placeholder={t("Tema")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="light">
            <span className="flex items-center gap-2">
              <Sun size={16} />
              {t("Tema claro")}
            </span>
          </SelectItem>
          <SelectItem value="dark">
            <span className="flex items-center gap-2">
              <Moon size={16} />
              {t("Tema escuro")}
            </span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
